// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import cartLineSharedStyles from "@awc/App/SharedStyles/CartLineSharedStyles";
import { productListLineStyles } from "@awc/Overrides/Widgets/MyListsDetails/MyListsDetailsProductListLine";
import { fontConfig } from "@awc/XDL/PreStyle/typography";
import {
    color,
    createLinkIconStyles,
    displayFlexOnPrint,
    sharedProductDescriptionStyles,
    xScreen,
} from "@awc/XDL/Utilities";
import { mergeWithCss } from "@awc/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { IconWrapper } from "@insite/mobius/Icon";
import Check from "@insite/mobius/Icons/Check";
import X from "@insite/mobius/Icons/X";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { css } from "styled-components";

// ============== Checbox Section ===================//
productListLineStyles.selectedCheckbox = {
    css: css`
        @media print {
            display: none;
        }
        margin: 0;
    `,
};
// ================================================== //

// ================ Image Section ===================== //
productListLineStyles.productImageStyles = {
    linkWrappingImage: {
        css: css`
            width: 120px;
            height: 120px;
            justify-content: center;
            ${xScreen("lte", "md")} {
                width: 99px;
                height: 99px;
            }
        `,
    },
    img: {
        css: css`
            aspect-ratio: 1;
            object-fit: contain;
        `,
    },
};
// ================================================== //

// ================ Info Section ==================== //
productListLineStyles.productDescriptionStyles = {
    ...sharedProductDescriptionStyles(),
};

productListLineStyles.restrictedDescriptionText = {
    ...sharedProductDescriptionStyles().productDetailDescription,
};

productListLineStyles.partNumbersSection = {
    css: css`
        display: flex;
        justify-content: space-between;
        margin-bottom: 12px;
        > ${StyledWrapper},>${TypographyStyle} {
            width: 50%;
        }
        ${xScreen("lte", "md")} {
            flex-direction: column;
            row-gap: 8px;
            > ${StyledWrapper},>${TypographyStyle} {
                width: 100%;
            }
        }
    `,
};

productListLineStyles.productPartNumbersStyles = {
    ...mergeWithCss(cartLineSharedStyles.productPartNumbers, {
        container: {
            css: css`
                margin-bottom: 0;
            `,
        },
    }),
};

productListLineStyles.checkboxSortOrderWrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        gap: 20px;
        width: 36px;
        ${({ editingSortOrder }: any) =>
            editingSortOrder &&
            `  align-items: center;
        `}

        ${xScreen("lte", "sm")} {
            flex-direction: row;
            gap: 32px;
        }
    `,
};

productListLineStyles.sortOrderGridItem = {
    width: 12,
    css: css`
        align-self: start;
        @media print {
            display: none;
        }
    `,
};
productListLineStyles.sortOrderTextField = {
    sizeVariant: "small",
    cssOverrides: {
        formInputWrapper: css`
            width: 36px;
            & > input {
                text-align: center;
            }
        `,
    },
    labelProps: {
        css: css`
            width: auto;
        `,
    },
};

productListLineStyles.createdOnText = {
    size: fontConfig.fontSize.regular,
    lineHeight: fontConfig.lineHeight.small,
    italic: true,
    weight: "normal",
    color: "common.ui4",
    css: css`
        display: block;
    `,
};

productListLineStyles.restrictedMessageText = {
    css: css`
        margin-top: 12px;
    `,
};

// ================ Actions and price Section ================== //
productListLineStyles.quantityBreakPricing = {
    viewLink: {
        typographyProps: {
            size: fontConfig.fontSize.xSmall,
        },
        css: css`
            margin-bottom: 8px;
        `,
    },
};

productListLineStyles.productPriceStyles = {
    wrapper: {
        css: css`
            width: auto;
        `,
    },
    priceWrapper: {
        css: css`
            line-height: ${fontConfig.lineHeight.xSmall};
            margin-bottom: 4px;
        `,
    },
    price: {
        priceText: {
            size: 18,
            weight: fontConfig.weight.bold,
            lineHeight: "24px",
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                size: fontConfig.fontSize.xSmall,
                lineHeight: fontConfig.lineHeight.xxSmall,
                weight: fontConfig.weight.regular,
                css: css`
                    white-space: nowrap;
                `,
            },
        },
    },
    quoteMessageWrapper: {
        css: css`
            margin-bottom: 4px;
            line-height: 24px;
            display: block;
        `,
    },
    unitOfMeasure: {
        unitOfMeasureText: {
            size: fontConfig.fontSize.xSmall,
            lineHeight: fontConfig.lineHeight.xxSmall,
            weight: fontConfig.weight.regular,
            css: css`
                white-space: nowrap;
            `,
        },
    },
};

productListLineStyles.addToCartButton = {
    mergeCss: true,
    sizeVariant: "small",
    css: css`
        flex-grow: 1;
    `,
};

productListLineStyles.quantityAddtoCartWrapper = {
    css: css`
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        width: 100%;
        gap: 8px;
        justify-content: space-between;
        @media print {
            flex-direction: column;
            align-items: flex-start;
            gap: 5px;
        }
    `,
};
productListLineStyles.updateQuantityLink = {
    ...createLinkIconStyles({
        icon: "RefreshCw",
        linkStyles: {
            typographyProps: {
                size: fontConfig.fontSize.xSmall,
                weight: fontConfig.weight.bold,
                css: css`
                    line-height: ${fontConfig.lineHeight.xxSmall};
                `,
            },
        },
    }),
};
productListLineStyles.quantityUpdatedWrapper = {
    css: css`
        line-height: ${fontConfig.lineHeight.xxSmall};
        @media print {
            display: none;
        }
    `,
};
productListLineStyles.quantityUpdatedIcon = {
    src: Check,
    color: "success",
    size: 11,
    css: css`
        margin-right: 3px;
    `,
};
productListLineStyles.quantityUpdatedText = {
    size: fontConfig.fontSize.xSmall,
    lineHeight: fontConfig.lineHeight.xxSmall,
    weight: fontConfig.weight.bold,
    color: "success",
};

productListLineStyles.productAvailabilityStyles = {
    container: {
        css: css`
            margin-bottom: 8px;
        `,
        offsetCss: css`
            & > div:empty {
                display: none;
            }
        `,
    },
    errorWrapper: {
        css: css`
            margin-bottom: 12px;
        `,
    },
    messageGridItem: {
        width: 12,
        css: css`
            ${displayFlexOnPrint}
        `,
    },
};

productListLineStyles.productUnitOfMeasureSelectStyles = {
    labelProps: {
        css: css`
            display: none;
            @media print {
                display: block;
            }
        `,
    },
    sizeVariant: "small",
    cssOverrides: {
        inputSelect: css`
            margin: 0;
        `,
        formField: css`
            width: auto;
            ${IconWrapper} {
                background: none;
            }
        `,
    },
};

productListLineStyles.productQuantityOrdered = {
    wrapper: {
        css: css`
            width: 70px;
            min-width: 70px;
            margin-top: 5px;
        `,
    },
    editableQuantityTextField: {
        cssOverrides: {
            inputSelect: css`
                height: 32px;
            `,
        },
    },
};

productListLineStyles.deleteLink = {
    typographyProps: {
        size: fontConfig.fontSize.regular,
        lineHeight: fontConfig.lineHeight.small,
    },
    css: css`
        display: inline-block;
        margin-left: 10px;

        &:focus {
            outline: none;
        }
    `,
};

productListLineStyles.deleteItemWrapper = {
    css: css`
        display: flex;
        align-items: center;
        gap: 10px;
        width: 100%;
        line-height: ${fontConfig.lineHeight.xxSmall};
    `,
};

productListLineStyles.updateQTY = {
    css: css`
        height: 17px;
        margin-top: 8px;
        ${xScreen("lte", "md")} {
            height: auto;
            margin-top: 12px;
        }
    `,
};

productListLineStyles.editNotesLink = {
    icon: {
        iconProps: {
            size: 12,
            css: css`
                margin-right: 6px;
            `,
        },
    },
    typographyProps: {
        size: fontConfig.fontSize.xSmall,
    },
    css: css`
        display: flex;
        justify-content: center;
        ${xScreen("lte", "md")} {
            margin-top: 12px;
        }
    `,
};

productListLineStyles.purchasedDateGridItem = {
    width: 12,
    css: css`
        flex-direction: column;
        padding: 0 0 10px 0;
    `,
};

productListLineStyles.purchasedDateLabelText = {
    ...mergeWithCss(productListLineStyles.purchasedDateLabelText, {
        css: css`
            margin-bottom: 0;
        `,
    }),
};

// ==================== Notes Section ============================ //
productListLineStyles.editableNotesTextArea = {
    css: css`
        &&&& {
            overflow: auto;
            @media print {
                border: 0;
                overflow: hidden;
                max-width: 100%;
                box-shadow: none;
            }
        }
        max-width: 50%;
        ${xScreen("lte", "md")} {
            min-width: 100%;
            max-width: 100%;
        }
    `,
    labelProps: {
        css: css`
            @media print {
                text-align: left;
                font-weight: ${fontConfig.weight.medium};
            }
        `,
    },
};

// ===================================================== //

productListLineStyles.listLineContainerStyles = {
    container: {
        css: css`
            ${({ editingSortOrder }: any) =>
                editingSortOrder &&
                `grid-template-columns: 52px 140px minmax(0, 700px) minmax(40px, 1fr) minmax(0, 317px);
                
                `};
            ${xScreen("lte", "md")} {
                ${({ editingSortOrder }: any) =>
                    editingSortOrder &&
                    ` grid-template-columns: 52px 112px minmax(0,700px)minmax(40px,1fr)minmax(0,317px);
                    `};
            }
            ${xScreen("lte", "sm")} {
                grid-template-columns: 0 112px minmax(0, 700px) minmax(40px, 1fr) minmax(0, 317px);
            }
        `,
    },
};

productListLineStyles.noteWrapper = {
    ...cartLineSharedStyles?.cartLineNotes?.wrapper,
};
productListLineStyles.noteLabel = {
    ...mergeWithCss(cartLineSharedStyles?.cartLineNotes?.label, {
        css: css`
            button {
                border: none;
            }
        `,
    }),
};

productListLineStyles.noteTextFieldWrapper = {
    ...cartLineSharedStyles?.cartLineNotes?.textFieldWrapper,
};

productListLineStyles.editableNotesTextField = {
    ...cartLineSharedStyles?.cartLineNotes?.editableNotesTextField,
};
